import * as THREE from "three";
import * as Tone from "tone";
import { Midi } from "@tonejs/midi";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Scene } from "./scene.js";
import { Camera } from "./camera.js";
import { Renderer } from "./renderer.js";
import { Loop } from "./animation.js";
import { Resizer } from "./resizer.js";
import { House } from "../objects/house.js";
import { Hill } from "../objects/hill.js";
import { makeFrame, setTabsHidden } from "./border.js";

import { Landscape } from "../objects/landscape.js";
import {
  piano,
  keyMap,
  keyOrder,
  baseOctave,
  noteForKey,
  initAudio,
  pianoReady,
  audioState,
} from "./audio.js";

class App {
  refreshFrameRefs = () => {
    this.octaveDisplay = document.getElementById("octave-bottom");
    this.posXEl = document.getElementById("pos-x");
    this.posYEl = document.getElementById("pos-y");
    this.posZEl = document.getElementById("pos-z");
    this.lookXEl = document.getElementById("look-x");
    this.lookYEl = document.getElementById("look-y");
    this.lookZEl = document.getElementById("look-z");
  };

  refreshSongUIRefs = () => {
    this.songSelect = document.getElementById("song-select");
    this.songPlayToggle = document.getElementById("song-play-stop");
    this.songStatus = document.getElementById("song-status");
  };

  constructor(container) {
    console.log("new app");
    this.keyObjects = {};
    this.noteToKey = {};
    this.currentOctave = baseOctave;
    this.raycaster = new THREE.Raycaster();
    this.octaveDisplay = document.getElementById("octave-bottom");
    this.songSelect = document.getElementById("song-select");
    this.songPlayToggle = document.getElementById("song-play-stop");
    this.songStatus = document.getElementById("song-status");
    // this.frameToggle = document.getElementById("frame-toggle");
    this.loadedSong = null;
    this.isSongPlaying = false;
    this.toneIndex = 0;
    this.landscapes = {};
    this.tabsHidden = false;
    this.floatingNotes = [];
    this.lastFloatingUpdate = null;

    this.updateOctaveDisplay();
    this.setupSongUI();

    window.addEventListener("click", async () => {
      await initAudio();
      piano.releaseAll();
    });

    window.addEventListener("keydown", (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        this.cycleTone();
        return;
      }
      if (!pianoReady) return;
    });

    window.addEventListener("keydown", (e) => {
      if (e.key >= "1" && e.key <= "8") {
        this.setActiveOctave(parseInt(e.key, 10));
        return;
      }
      const entry = keyMap[e.key];
      if (!entry) return;
      const keyId = `${e.key}-${this.currentOctave}`;
      if (audioState.activeKeys[keyId]) return;
      audioState.activeKeys[keyId] = true;
      const resolved = noteForKey(e.key, this.currentOctave);
      if (!resolved) return;
      piano.triggerAttack(resolved);
      this.noteToKey[resolved] = { key: e.key, octave: this.currentOctave };
      Tone.Draw.schedule(() => {
        const keyObj = this.keyObjects[this.currentOctave]?.[e.key];
        if (keyObj) {
          this.visualNoteOn(keyObj, resolved);
        }
      }, Tone.now());
    });

    window.addEventListener("keyup", (e) => {
      const entry = keyMap[e.key];
      if (!entry) return;
      const keyId = `${e.key}-${this.currentOctave}`;
      audioState.activeKeys[keyId] = false;
      if (audioState.sustainOn) {
        const resolved = noteForKey(e.key, this.currentOctave);
        if (resolved) {
          audioState.heldNotes.add(resolved);
          this.noteToKey[resolved] = { key: e.key, octave: this.currentOctave };
        }
      } else {
        const resolved = noteForKey(e.key, this.currentOctave);
        if (resolved) {
          piano.triggerRelease(resolved);
        }
      }

      Tone.Draw.schedule(() => {
        const keyObj = this.keyObjects[this.currentOctave]?.[e.key];
        if (keyObj) {
          this.visualNoteOff(keyObj, { skipLightOff: audioState.sustainOn });
        }
      }, Tone.now());
    });

    this.scene = new Scene();
    this.camera = new Camera();
    this.renderer = new Renderer();
    this.loop = new Loop(this.camera, this.scene, this.renderer);

    this.refreshFrameRefs();
    this.camTarget = new THREE.Vector3(0, 1, 0);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.target.copy(this.camTarget);
    this.controls.maxPolarAngle = Math.PI * 0.5 - 0.05; // keep camera above ground plane
    this.controls.update();

    // keep UI updated with camera state
    this.loop.updatables.push({
      tick: () => {
        this.controls.update();
        this.camTarget.copy(this.controls.target);
      },
    });
    this.loop.updatables.push({
      tick: () => {
        this.updateFloatingNotes();
      },
    });
    this.loop.updatables.push({
      tick: () => {
        this.updateCameraInfo();
      },
    });

    //helpers
    // const axesHelper = new THREE.AxesHelper(3);
    // this.scene.add(axesHelper);
    // const gridHelper = new THREE.GridHelper(10, 10);
    // this.scene.add(gridHelper);

    this.setActiveOctave(this.currentOctave);

    container.append(this.renderer.domElement);
    new Resizer(container, this.camera, this.renderer);

    // Initialize the border frame
    makeFrame();
    this.refreshFrameRefs();
    this.refreshSongUIRefs();
    this.setupSongUI();
    this.applyFrameHiddenState(this.tabsHidden);

    window.addEventListener("click", (e) => {
      if (this.isUIClickTarget(e.target)) return;
      this.toggleFrameHidden();
    });

    window.addEventListener("resize", () =>
      requestAnimationFrame(() => {
        makeFrame();
        this.refreshFrameRefs();
        this.refreshSongUIRefs();
        this.setupSongUI();
        this.applyFrameHiddenState(this.tabsHidden);
      })
    );

    this.loop.start();
  }

  ensureLandscape(octave) {
    if (this.landscapes[octave]) return this.landscapes[octave];
    const mtn = new Landscape(this.loop, octave);
    this.landscapes[octave] = mtn;
    this.scene.add(mtn);
    mtn.ready.then(() => {
      this.buildPiano(mtn, octave);
    });
    return mtn;
  }

  setActiveOctave(octave) {
    this.currentOctave = octave;
    this.updateOctaveDisplay();
    return this.ensureLandscape(octave);
  }

  buildPiano(mtn, octave) {
    const anchor = mtn && mtn.anchor ? mtn.anchor : new THREE.Vector3(0, 0, 0);
    const innerSpacing = 0.8;
    const blackRowOffset = 0.6;
    const startZ = anchor.z - ((keyOrder.length - 3) * innerSpacing) / 2;

    if (!this.keyObjects[octave]) {
      this.keyObjects[octave] = {};
    }
    let acrossCount = 0;

    keyOrder.forEach((key, idx) => {
      const isSharp = (keyMap[key]?.note || "").includes("#");
      const x = anchor.x + (isSharp ? blackRowOffset : 0);
      const z = startZ + acrossCount * innerSpacing;
      if (!isSharp) {
        acrossCount++;
      }

      const surface = this.sampleSurfaceOnMountain(mtn, x, z, anchor.y);
      const hillPos = new THREE.Vector3(x, surface.y, z);
      if (surface.normal) {
        hillPos.addScaledVector(surface.normal, -0.4);
      }
      const house = new House(this.loop, hillPos.x, hillPos.z, hillPos.y);
      const hill = new Hill(this.loop, hillPos.x, hillPos.z, hillPos.y);
      // hill.add(house);
      if (surface.normal) {
        hill.setNormal(surface.normal);
      }
      mtn.add(house);
      mtn.add(hill);

      this.keyObjects[octave][key] = {
        house,
        hill,
        x,
        y: 0,
        octave,
      };
    });
  }

  sampleSurfaceOnMountain(mtn, x, z, fallbackY = 0) {
    if (!mtn || !mtn.mountainMesh)
      return { y: fallbackY, normal: new THREE.Vector3(0, 1, 0) };
    mtn.updateMatrixWorld(true);
    const origin = new THREE.Vector3(x, fallbackY + 50, z);
    const dir = new THREE.Vector3(0, -1, 0);

    this.raycaster.set(origin, dir);
    const hits = this.raycaster.intersectObject(mtn.mountainMesh, true);
    if (hits.length > 0) {
      const hit = hits[0];
      const normal = hit.face.normal
        .clone()
        .applyMatrix3(
          new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld)
        )
        .normalize();
      return { y: hit.point.y, normal };
    }

    return { y: fallbackY, normal: new THREE.Vector3(0, 1, 0) };
  }

  setupSongUI() {
    if (this.songSelect) {
      this.songSelect.addEventListener("change", () => {
        const file = this.songSelect.value;
        console.log("file", file);
        if (!file) {
          this.loadedSong = null;
          this.setSongStatus("No song selected");
          return;
        }
        this.loadSongData(file);
      });
    }
    if (this.songPlayToggle) {
      this.songPlayToggle.addEventListener("click", async () => {
        if (this.isSongPlaying) {
          this.stopSong();
        } else {
          await this.playLoadedSong();
        }
      });
    }
  }

  setSongStatus(text) {
    if (this.songStatus) {
      this.songStatus.textContent = text;
    }
  }

  async loadSongData(file) {
    try {
      this.setSongStatus(`Loading ${file}...`);
      const isMidi =
        file?.toLowerCase().endsWith(".mid") ||
        file?.toLowerCase().endsWith(".midi");
      if (isMidi) {
        const res = await fetch(`/data/${file}`);
        if (!res.ok) {
          throw new Error(`Failed to load ${file} (${res.status})`);
        }
        const buffer = await res.arrayBuffer();
        const midi = new Midi(buffer);
        this.loadedSong = this.normalizeMidi(midi, file);
      } else {
        const res = await fetch(`/data/${file}`);
        if (!res.ok) {
          throw new Error(`Failed to load ${file} (${res.status})`);
        }
        const json = await res.json();
        this.loadedSong = this.normalizeSong(json, file);
      }
      this.isSongPlaying = false;
      this.updateSongToggleIcon();
      if (this.loadedSong?.bpm) {
        Tone.Transport.bpm.value = this.loadedSong.bpm;
      }
      if (this.loadedSong?.timeSignature) {
        Tone.Transport.timeSignature = this.loadedSong.timeSignature;
      }
      this.setSongStatus(`Loaded ${this.loadedSong.name || file}`);
    } catch (err) {
      console.error(err);
      this.loadedSong = null;
      this.setSongStatus(`Error loading song: ${err.message}`);
    }
  }

  async playLoadedSong() {
    if (!this.loadedSong || !Array.isArray(this.loadedSong.events)) {
      this.setSongStatus("Select a song with events to play");
      return;
    }
    await initAudio();

    Tone.Transport.stop();
    Tone.Transport.cancel();
    this.clearVisualStates();

    const events = this.loadedSong.events;
    const startDelay = 0.1;
    events.forEach((ev) => {
      const note = ev.note;
      if (!note) return;
      const time = typeof ev.time === "number" ? ev.time : 0;
      const duration = typeof ev.duration === "number" ? ev.duration : 0.5;
      Tone.Transport.schedule((t) => {
        piano.triggerAttackRelease(note, duration, t);
        this.triggerVisualForNote(note, duration, t);
      }, time);
    });

    Tone.Transport.start(`+${startDelay}`);
    this.isSongPlaying = true;
    this.updateSongToggleIcon();
    this.setSongStatus(
      `Playing ${this.loadedSong.name || this.songSelect?.value || "song"}`
    );
  }

  stopSong() {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    this.isSongPlaying = false;
    this.clearVisualStates();
    this.setSongStatus("Stopped");
    this.updateSongToggleIcon();
  }

  resolveKeyObjectForNote(note) {
    const match = note.match(/^([A-G]#?)(\d)$/);
    if (!match) return null;
    const [, letter, octaveStr] = match;
    const noteOctave = parseInt(octaveStr, 10);
    for (const key in keyMap) {
      const entry = keyMap[key];
      if (entry.note === letter) {
        const octave = noteOctave - (entry.offset || 0);
        const keyObj = this.keyObjects[octave]?.[key];
        if (keyObj) {
          return { keyObj, octave, key };
        }
        // If the landscape for this octave is not built yet, ensure it exists
        this.ensureLandscape(octave);
      }
    }
    return null;
  }

  triggerVisualForNote(note, duration = 0.5, time) {
    const resolved = this.resolveKeyObjectForNote(note);
    if (!resolved) return;
    const { keyObj } = resolved;
    const startTime = time || Tone.now();
    const start = () => {
      this.visualNoteOn(keyObj, note);
    };
    const end = () => {
      this.visualNoteOff(keyObj);
    };

    Tone.Draw.schedule(start, startTime);
    Tone.Draw.schedule(end, startTime + duration);
  }

  clearVisualStates() {
    Object.values(this.keyObjects).forEach((octObj) => {
      Object.values(octObj).forEach((keyObj) => {
        keyObj.hill.stretch(1);
        keyObj.house.setHillHeight(keyObj.hill.getHeightOffsetForStretch(1));
        keyObj.house.setLit(false);
      });
    });
  }

  visualNoteOn(keyObj, noteText) {
    const targetStretch = 2;
    keyObj.house.setLit(true);
    keyObj.hill.stretch(targetStretch);
    keyObj.house.setHillHeight(
      keyObj.hill.getHeightOffsetForStretch(targetStretch)
    );
    this.spawnFloatingNoteLabel(noteText, keyObj.house);
  }

  visualNoteOff(keyObj, options = {}) {
    const targetStretch = 1;
    keyObj.hill.stretch(targetStretch);
    keyObj.house.setHillHeight(
      keyObj.hill.getHeightOffsetForStretch(targetStretch)
    );
    if (!options.skipLightOff) {
      keyObj.house.setLit(false);
    }
  }

  spawnFloatingNoteLabel(noteText, house) {
    if (!house) return;
    const pos = new THREE.Vector3();
    house.getWorldPosition(pos);
    pos.y += 0.6;
    const sprite = this.makeNoteSprite(noteText);
    sprite.position.copy(pos);
    this.scene.add(sprite);
    this.floatingNotes.push({
      sprite,
      velocity: new THREE.Vector3(0, 1, 0),
      age: 0,
      life: 4,
    });
  }

  makeNoteSprite(text) {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      const fallbackMaterial = new THREE.SpriteMaterial({ color: "#ffffff" });
      const fallback = new THREE.Sprite(fallbackMaterial);
      fallback.scale.set(1, 0.4, 1);
      return fallback;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = "80px 'Baskervvile'";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText(
      String(text).toUpperCase(),
      canvas.width / 2,
      canvas.height / 2
    );

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    });
    const sprite = new THREE.Sprite(material);
    const width = 1.2;
    const height = width * (canvas.height / canvas.width);
    sprite.scale.set(width, height, 1);
    return sprite;
  }

  updateFloatingNotes() {
    const now = performance.now();
    const delta = this.lastFloatingUpdate
      ? Math.min((now - this.lastFloatingUpdate) / 1000, 0.05)
      : 0;
    this.lastFloatingUpdate = now;

    for (let i = this.floatingNotes.length - 1; i >= 0; i--) {
      const item = this.floatingNotes[i];
      item.age += delta;
      if (item.sprite?.position && item.velocity) {
        item.sprite.position.addScaledVector(item.velocity, delta);
        item.sprite.scale.multiplyScalar(1 - delta * 0.2);
        item.sprite.position.x +=
          Math.sin(performance.now() * 0.001 + i) * delta * 2;
      }
      const opacity = 1 - item.age / item.life;
      if (item.sprite?.material) {
        item.sprite.material.opacity = Math.max(opacity, 0);
      }
      if (item.age >= item.life) {
        this.scene.remove(item.sprite);
        if (item.sprite?.material?.map) item.sprite.material.map.dispose();
        if (item.sprite?.material) item.sprite.material.dispose();
        this.floatingNotes.splice(i, 1);
      }
    }
  }

  normalizeSong(json, fileName = "") {
    const events = [];

    if (Array.isArray(json?.events)) {
      json.events.forEach((ev) => {
        if (ev?.note) {
          events.push({
            time: ev.time ?? 0,
            duration: ev.duration ?? 0.5,
            note: ev.note,
          });
        }
      });
    }

    if (events.length === 0 && Array.isArray(json?.notes)) {
      json.notes.forEach((n) => {
        if (n?.pitch) {
          events.push({
            time: n.time ?? 0,
            duration: n.duration ?? 0.5,
            note: n.pitch,
          });
        }
      });
    }

    return {
      name: json?.name || fileName,
      bpm: json?.bpm || json?.tempo || 120,
      timeSignature: json?.timeSignature || json?.meter || [4, 4],
      events,
    };
  }

  normalizeMidi(midi, fileName = "") {
    const events = [];
    const limitSeconds = 30;

    midi.tracks.forEach((track) => {
      track.notes.forEach((n) => {
        if (typeof n.time !== "number") return;
        if (n.time >= limitSeconds) return;
        const remaining = limitSeconds - n.time;
        const duration = Math.max(0, Math.min(n.duration ?? 0.5, remaining));
        if (duration <= 0) return;
        events.push({
          time: n.time,
          duration,
          note: n.name,
        });
      });
    });

    events.sort((a, b) => a.time - b.time);

    const tempoEntry = midi.header.tempos?.[0];
    const bpm = tempoEntry?.bpm || midi.header?.bpm || 120;
    const tsEntry = midi.header.timeSignatures?.[0];
    const timeSignature = tsEntry?.timeSignature || [4, 4];

    return {
      name: midi.header?.name || fileName,
      bpm,
      timeSignature,
      events,
    };
  }

  updateOctaveDisplay() {
    if (this.octaveDisplay) {
      this.octaveDisplay.textContent = `OCTAVE SELECTED: ${this.currentOctave}`;
    }
  }

  cycleTone() {
    this.toneIndex = (this.toneIndex + 1) % 3;
    if (this.scene?.applyTone) {
      this.scene.applyTone(this.toneIndex);
    }
  }

  updateCameraInfo() {
    const p = this.camera.position;
    if (this.posXEl) this.posXEl.textContent = `x:${p.x.toFixed(2)}`;
    if (this.posYEl) this.posYEl.textContent = `y:${p.y.toFixed(2)}`;
    if (this.posZEl) this.posZEl.textContent = `z:${p.z.toFixed(2)}`;

    const t = this.camTarget;
    if (this.lookXEl) this.lookXEl.textContent = `lx:${t.x.toFixed(2)}`;
    if (this.lookYEl) this.lookYEl.textContent = `ly:${t.y.toFixed(2)}`;
    if (this.lookZEl) this.lookZEl.textContent = `lz:${t.z.toFixed(2)}`;
  }

  updateSongToggleIcon() {
    if (!this.songPlayToggle) return;
    this.songPlayToggle.src = this.isSongPlaying ? "/pause.png" : "/play.png";
  }

  isUIClickTarget(target) {
    if (!target || typeof target.closest !== "function") return false;
    return Boolean(
      target.closest("#ui") ||
        target.closest(".display") ||
        target.closest(".song-controls")
    );
  }

  applyFrameHiddenState(hidden) {
    this.tabsHidden = hidden;
    setTabsHidden(hidden);
    const display = document.querySelector(".display");
    if (display) display.classList.toggle("faded", hidden);
  }

  toggleFrameHidden() {
    this.applyFrameHiddenState(!this.tabsHidden);
  }

  setupCameraPane() {
    const pane = new Pane({ title: "Camera" });
    pane.element.style.position = "fixed";
    pane.element.style.top = "12px";
    pane.element.style.right = "12px";
    pane.element.style.zIndex = "20";

    const posFolder = pane.addFolder({ title: "Position" });
    posFolder.addInput(this.camera.position, "x", {
      min: -30,
      max: 30,
      step: 0.1,
    });
    posFolder.addInput(this.camera.position, "y", {
      min: 0,
      max: 30,
      step: 0.1,
    });
    posFolder.addInput(this.camera.position, "z", {
      min: -30,
      max: 30,
      step: 0.1,
    });

    const targetFolder = pane.addFolder({ title: "Target" });
    targetFolder
      .addInput(this.camTarget, "x", { min: -10, max: 10, step: 0.1 })
      .on("change", () => this.applyCameraTarget());
    targetFolder
      .addInput(this.camTarget, "y", { min: -10, max: 10, step: 0.1 })
      .on("change", () => this.applyCameraTarget());
    targetFolder
      .addInput(this.camTarget, "z", { min: -10, max: 10, step: 0.1 })
      .on("change", () => this.applyCameraTarget());

    this.pane = pane;
  }

  applyCameraTarget() {
    if (!this.controls) return;
    this.controls.target.copy(this.camTarget);
    this.controls.update();
  }
}

export default App;
